import { useEffect, useState, useCallback } from "react";
import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

const upcomingFilter = () => new Date().toISOString();

/** Public website: published + visibility public only (anon key). */
export function usePublicEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error: err } = await supabase
        .from("events")
        .select("*")
        .eq("published", true)
        .eq("visibility", "public")
        .gte("starts_at", upcomingFilter())
        .order("starts_at", { ascending: true });
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setEvents([]);
      } else {
        setError(null);
        setEvents(data || []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { events, loading, error };
}

/** Logged-in member: RLS returns public, members_only, and invite_only if invited. */
export function useMemberUpcomingEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!isSupabaseConfigured || !supabase) {
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: err } = await supabase
      .from("events")
      .select("*")
      .eq("published", true)
      .gte("starts_at", upcomingFilter())
      .order("starts_at", { ascending: true });
    if (err) {
      setError(err.message);
      setEvents([]);
    } else {
      setError(null);
      setEvents(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { events, loading, error, refresh };
}

export function useMemberInvitations(userId) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!userId || !supabase) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("event_invitations")
      .select("id, status, invited_at, events(*)")
      .eq("user_id", userId)
      .order("invited_at", { ascending: false });
    const list = (data || [])
      .map((r) => ({ ...r, event: r.events }))
      .filter((r) => r.event && new Date(r.event.starts_at) >= new Date());
    setRows(list);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { invitations: rows, loading, refresh };
}

export function formatEventWhen(startsAt, endsAt) {
  if (!startsAt) return "";
  const s = new Date(startsAt);
  const opts = { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" };
  let t = s.toLocaleString(undefined, opts);
  if (endsAt) {
    const e = new Date(endsAt);
    t += " – " + e.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  return t;
}
